import { z } from 'zod/v4';

import { THEME_BUTTON_STYLE_TYPES, ThemeColorNames } from '@sites/site-theme';

import { EButtonHoverType, type EButtonStyleType } from '@common/enums';
import { CSS_RESTRICTIONS } from '@common/constants/css-restrictions.const';

import { firstDefined, normalizeFlatArgs, parseIfJsonText } from '../widget-schemas/fold-helpers';

/**
 * AI-facing schema for `update_theme_button` — how one of the theme's two button styles looks,
 * everywhere it appears.
 *
 * **Two styles, `primary` and `secondary`, and no others.** `EButtonStyleType` also names `classic`
 * and `custom`; a THEME has never carried them (`THEME_BUTTON_STYLE_TYPES`, and the settings panel
 * shows exactly two tabs), so offering them here would let a caller write a button nothing renders.
 *
 * **Colours are single strings, stored as one-element arrays.** The persisted shape is
 * `string | string[]` because a colour may have several stops, but every shipped preset and every
 * panel write is a single value. Exposing the array would make the common case harder to get right
 * for nothing.
 *
 * **Hover fields are prefixed rather than nested.** Flat is this surface's house style, and
 * `color`/`hoverColor` side by side is the one spelling in which a model cannot lose track of which
 * state it is editing.
 */
export const THEME_BUTTON_STYLES = THEME_BUTTON_STYLE_TYPES as readonly EButtonStyleType[];

const colorField = (what: string) =>
    z
        .string()
        .optional()
        .describe(
            `${what} Either a theme palette name (${Object.values(ThemeColorNames).join(', ')}), which keeps the ` +
                'button following the site, or a CSS colour like "#1D4ED8" or "rgba(0,0,0,0)" for transparent.'
        );

const updateThemeButtonObjectSchema = z.object({
    styleType: z
        .enum(THEME_BUTTON_STYLES as [EButtonStyleType, ...EButtonStyleType[]])
        .describe('Which of the theme\'s two button styles to change: "primary" or "secondary".'),

    color: colorField('Label colour at rest.'),
    background: colorField('Fill colour at rest.'),
    borderColor: colorField('Contour colour at rest.'),
    borderStyle: z.string().optional().describe('Contour style — "solid", "dashed", "dotted", "none".'),
    borderWidth: z
        .number()
        .min(CSS_RESTRICTIONS.BORDER_WIDTH_MIN)
        .max(CSS_RESTRICTIONS.BORDER_WIDTH_MAX)
        .optional()
        .describe(
            `Contour thickness in PIXELS, ${CSS_RESTRICTIONS.BORDER_WIDTH_MIN}–${CSS_RESTRICTIONS.BORDER_WIDTH_MAX}.`
        ),
    borderRadius: z
        .number()
        .min(CSS_RESTRICTIONS.BORDER_RADIUS_MIN)
        .max(CSS_RESTRICTIONS.BORDER_RADIUS_MAX)
        .optional()
        .describe(
            `Corner rounding in PIXELS, ${CSS_RESTRICTIONS.BORDER_RADIUS_MIN}–` +
                `${CSS_RESTRICTIONS.BORDER_RADIUS_MAX}. 0 is a square corner; a large value makes a pill.`
        ),

    hoverType: z
        .enum(EButtonHoverType)
        .optional()
        .describe(
            'What happens on hover: "opacity" fades the button, "shadow" casts one, "color" swaps to the ' +
                "hover colours below. Switching this resets the hover state to that effect's defaults, so send " +
                'any hover colours you want in the SAME call.'
        ),
    hoverOpacity: z
        .number()
        .min(CSS_RESTRICTIONS.OPACITY_MIN)
        .max(CSS_RESTRICTIONS.OPACITY_MAX)
        .optional()
        .describe(
            `Opacity on hover, ${CSS_RESTRICTIONS.OPACITY_MIN}–${CSS_RESTRICTIONS.OPACITY_MAX} percent. ` +
                'Only has an effect when hoverType is "opacity".'
        ),
    hoverShadow: z
        .string()
        .optional()
        .describe('CSS box-shadow used when hoverType is "shadow", e.g. "0 0 10px 6px rgb(0 0 0 / 10%)".'),
    hoverColor: colorField('Label colour on hover; only applies when hoverType is "color".'),
    hoverBackground: colorField('Fill colour on hover; only applies when hoverType is "color".'),
    hoverBorderColor: colorField('Contour colour on hover; only applies when hoverType is "color".'),
});

export type UpdateThemeButtonArgs = z.infer<typeof updateThemeButtonObjectSchema>;

/**
 * Lifts the nested and everyday spellings.
 *
 * `{ base: {...}, hover: {...} }` is the STORED shape, and the snapshot will eventually echo it, so
 * a model answering in it is being reasonable — `hover`'s keys are prefixed on the way out so they
 * do not collide with the rest state's. `type` for `styleType` and `radius` for `borderRadius` are
 * the words the rest of this tool surface has already had to accept elsewhere.
 */
function foldButton(args: Record<string, unknown>): void {
    // Both containers go through `parseIfJsonText`: the leaked-XML path stringifies every argument,
    // so the stored shape this fold exists to accept arrives as text rather than as an object.
    const base = parseIfJsonText(args['base']);

    if (base && typeof base === 'object' && !Array.isArray(base)) {
        for (const [key, value] of Object.entries(base as Record<string, unknown>)) {
            if (args[key] === undefined) {
                args[key] = value;
            }
        }
    }

    delete args['base'];

    const hover = parseIfJsonText(args['hover']);

    if (hover && typeof hover === 'object' && !Array.isArray(hover)) {
        for (const [key, value] of Object.entries(hover as Record<string, unknown>)) {
            const prefixed = key === 'hoverType' ? 'hoverType' : `hover${key[0].toUpperCase()}${key.slice(1)}`;

            if (args[prefixed] === undefined) {
                args[prefixed] = value;
            }
        }
    }

    delete args['hover'];

    const synonyms: Readonly<Record<string, readonly string[]>> = {
        styleType: ['type', 'buttonType', 'buttonStyle'],
        // `textColor` is not a guess at what a model might say — it is a real field name on the FORM
        // widget's surface (`textColor`, `inputTextColor`), so a model that has just read that tool
        // brings the word here. Observed live 2026-08-31: `update_theme_button` was called with
        // `{ styleType: 'primary', textColor: 'primary', borderWidth: '2' }`, the unknown key was
        // stripped by the plain object schema, the only surviving field already held its value, and
        // the caller was told "nothing changed" — which it read as "the colour is already right" and
        // reported to the user as such. The label colour never moved.
        color: ['textColor', 'labelColor', 'fontColor'],
        hoverColor: ['hoverTextColor', 'hoverLabelColor'],
        background: ['backgroundColor', 'bgColor', 'fill'],
        borderRadius: ['radius', 'cornerRadius'],
        borderWidth: ['borderSize'],
        hoverOpacity: ['opacity'],
        hoverShadow: ['shadow'],
        hoverBackground: ['hoverBackgroundColor'],
    };

    for (const [target, keys] of Object.entries(synonyms)) {
        if (args[target] === undefined) {
            const value = firstDefined(...keys.map((key) => args[key]));

            if (value !== undefined) {
                args[target] = value;
            }
        }

        for (const key of keys) {
            delete args[key];
        }
    }
}

export const updateThemeButtonArgsSchema = z.preprocess(
    (raw) => normalizeFlatArgs(raw, foldButton),
    updateThemeButtonObjectSchema
);

/**
 * The argument names this tool understands, after folding — exported so a caller can report the ones
 * it did NOT. A plain `z.object()` strips an unknown key with no issue raised, which is how
 * `textColor` became "nothing changed" instead of "there is no such field".
 */
export const THEME_BUTTON_ARG_KEYS: readonly string[] = Object.keys(updateThemeButtonObjectSchema.shape);

/** Runs the same fold the schema does, so a caller can compare keys against what it would accept. */
export function normalizeThemeButtonArgs(raw: unknown): unknown {
    return normalizeFlatArgs(raw, foldButton);
}
